import { s, spacing } from '@/theme/tokens';
import { detailPageSectionGap } from '@/ui/composites/scaffolds/detailPageSpacing';
import { useMediaQuery, useTheme } from '@/ui/primitives/hooks';

export type StackedMetricsStatusChartPanelLayout = {
  height: number;
  chartHeight: number;
  headerHeight: number;
  headerToChartGap: number;
  panelPadding: 'md';
  panelContentGap: 'md';
  headerTitlePaddingLeft: string;
  headerControlsPaddingRight: string;
  yAxisWidth: number;
  yAxisAutoWidth: boolean;
  yAxisAutoWidthPadding: number;
  axisStrokeWidth: number;
  barCornerRadius: number;
  yAxisLabelOffset: number;
  hideXAxisTicks: boolean;
  xAxisLabelOffset: number;
  chartMargins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type StackedMetricsMiniPanelsLayout = {
  columnHeight: number;
  height: number;
  headerHeight: number;
  chartHeight: number;
  columnGap: string;
  rowGap: string;
  rowGapPx: number;
  panelPaddingTop: string;
  panelPaddingBottom: string;
  headerPaddingX: string;
  headerPaddingTop: string;
  headerPaddingBottom: string;
  contentPaddingX: string;
  contentPaddingTop: string;
  contentPaddingBottom: string;
  chartWrapperPaddingX: string;
  chartWrapperPaddingTop: string;
  chartWrapperPaddingBottom: string;
  yAxisWidth: number;
  axisStrokeWidth: number;
  yDomainPaddingTopRatio: number;
  yDomainPaddingBottomRatio: number;
  chartMargins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type StackedMetricsSectionLayout = {
  gap: string;
  rowGap: string;
  toolbarGap: string;
  bottomGap: string;
};

export type StackedMetricsStatusChartSectionLayout = {
  isNarrowChartLayout: boolean;
  isTwoColumnChartLayout: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  statusChartVisualHeight: number;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  miniPanels: StackedMetricsMiniPanelsLayout;
  sectionLayout: StackedMetricsSectionLayout;
};

export const useStackedMetricsStatusChartSectionLayout =
  (): StackedMetricsStatusChartSectionLayout => {
    const theme = useTheme();
    const isNarrowChartLayout = useMediaQuery(theme.breakpoints.down('lg'));
    const isTwoColumnChartLayout = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
    const miniPanelCount = 4;
    const chartComponentGap = s('xl');
    const metricsColumnHeight = 360;
    const sharedTwoColumnPanelHeight = 360;
    const miniPanelsColumnHeight = isTwoColumnChartLayout
      ? sharedTwoColumnPanelHeight
      : metricsColumnHeight;
    const miniPanelsRowGapPx = spacing.xl;
    const stackedSectionBaseRowHeight =
      (miniPanelsColumnHeight - miniPanelsRowGapPx * (miniPanelCount - 1)) / miniPanelCount;
    const piePanelHeight = isTwoColumnChartLayout
      ? sharedTwoColumnPanelHeight
      : isNarrowChartLayout
        ? 248
        : metricsColumnHeight;

    const statusPanel: StackedMetricsStatusChartPanelLayout = {
      height: isTwoColumnChartLayout
        ? sharedTwoColumnPanelHeight
        : isNarrowChartLayout
          ? 316
          : metricsColumnHeight,
      chartHeight: isTwoColumnChartLayout
        ? 244
        : isNarrowChartLayout
          ? 220
          : metricsColumnHeight - 42,
      headerHeight: 28,
      headerToChartGap: isNarrowChartLayout ? 20 : 24,
      panelPadding: 'md',
      panelContentGap: 'md',
      headerTitlePaddingLeft: s('md'),
      headerControlsPaddingRight: s('md'),
      yAxisWidth: 40,
      yAxisAutoWidth: true,
      yAxisAutoWidthPadding: 6,
      axisStrokeWidth: 0.75,
      barCornerRadius: 2,
      yAxisLabelOffset: -6,
      hideXAxisTicks: false,
      xAxisLabelOffset: 3,
      chartMargins: {
        left: -10,
        right: 16,
        top: 0,
        bottom: isNarrowChartLayout ? 4 : 8,
      },
    };

    const miniPanels: StackedMetricsMiniPanelsLayout = {
      columnHeight: miniPanelsColumnHeight,
      height: stackedSectionBaseRowHeight,
      headerHeight: 24,
      chartHeight: 44,
      columnGap: chartComponentGap,
      rowGap: chartComponentGap,
      rowGapPx: miniPanelsRowGapPx,
      panelPaddingTop: s('sm'),
      panelPaddingBottom: s('none'),
      headerPaddingX: s('xs'),
      headerPaddingTop: s('sm'),
      headerPaddingBottom: s('md'),
      contentPaddingX: s('none'),
      contentPaddingTop: s('none'),
      contentPaddingBottom: s('xxs'),
      chartWrapperPaddingX: s('none'),
      chartWrapperPaddingTop: '2px',
      chartWrapperPaddingBottom: s('none'),
      yAxisWidth: 0,
      axisStrokeWidth: 0.75,
      yDomainPaddingTopRatio: 0.16,
      yDomainPaddingBottomRatio: 0.05,
      chartMargins: {
        left: 0,
        right: 0,
        top: 5,
        bottom: -4,
      },
    };

    const sectionLayout: StackedMetricsSectionLayout = {
      gap: chartComponentGap,
      rowGap: chartComponentGap,
      toolbarGap: detailPageSectionGap,
      bottomGap: s('none'),
    };

    const pieChartVisualHeight = isNarrowChartLayout
      ? piePanelHeight - miniPanels.headerHeight - (isTwoColumnChartLayout ? 60 : 36)
      : piePanelHeight - miniPanels.headerHeight - 56;
    const statusChartVisualHeight = statusPanel.chartHeight;

    return {
      isNarrowChartLayout,
      isTwoColumnChartLayout,
      piePanelHeight,
      pieChartVisualHeight,
      statusChartVisualHeight,
      statusPanel,
      miniPanels,
      sectionLayout,
    };
  };
